import {Injectable} from '@angular/core';
import {VideoRecordingRestapi} from '@webmate-web-core/shared/api/rest/video-recording.restapi';
import {DeviceId, ProjectId, VideoRecordingId} from '@webmate-web-core/shared/types/ids';
import {Observable, switchMap} from 'rxjs';
import {VideoRecording} from '@webmate-web-core/shared/types/video';


export interface VideoMetaData {
    name: string,
    deviceId: string,
    size: number,
    type: string,
    duration: number,
    width: number,
    height: number
}


@Injectable({providedIn: 'root'})
export class VideoRecordingService {

    constructor(private api: VideoRecordingRestapi) {
    }

    getVideoRecording(videoId: VideoRecordingId): Observable<VideoRecording> {
        return this.api.getVideoRecording(videoId);
    }

    getVideos(projectId: ProjectId): Observable<Array<VideoRecording>> {
        return this.api.getVideos(projectId);
    }

    getVideosForDevice(deviceId: DeviceId, state?: string): Observable<Array<VideoRecording>> {
        return this.api.getVideosForDevice(deviceId, state);
    }

    getVideo(videoId: VideoRecordingId): Observable<Blob> {
        return this.api.getVideo(videoId);
    }

    getVideoUrl(videoId: VideoRecordingId): string {
        return this.api.getVideoUrl(videoId);
    }

    deleteVideo(videoId: VideoRecordingId): Observable<any> {
        return this.api.deleteVideo(videoId);
    }

    storeVideo(projectId: ProjectId, videoBlob: Blob, name: string, deviceId?: DeviceId): Observable<VideoRecordingId> {
        return this.extractVideoMetadata(videoBlob, name, deviceId).pipe(
            switchMap((metadata) => {
                return this.api.storeVideo(projectId, videoBlob, metadata);
            }),
        );
    }

    // Update the name of an image in the backend
    setVideoName(videoId: VideoRecordingId, name: string):Observable<any> {
        return this.getVideoRecording(videoId).pipe(
            switchMap(video => {
                if (video.metadata) {
                    let metadata = video.metadata;
                    metadata.name = name;
                    return this.api.setVideoMetadata(videoId, metadata);
                }
            })
        )
    }

    // Bound how long we wait for the browser to report video metadata. Some inputs
    // (streaming/unseekable or mislabeled files) load metadata but never fire the
    // 'timeupdate' our duration probe relies on; without this cap the returned
    // Observable would never settle and would hang any pipeline awaiting it.
    private static readonly METADATA_TIMEOUT_MS = 15_000;

    extractVideoMetadata(videoBlob: Blob, name: string, deviceId?: DeviceId): Observable<VideoMetaData> {
        return new Observable<VideoMetaData>((observer) => {
            const video: HTMLVideoElement = document.createElement('video');
            video.preload = 'metadata';
            const objectUrl = URL.createObjectURL(videoBlob);
            video.src = objectUrl;

            let settled = false;
            const cleanup = () => {
                video.onerror = null;
                video.onloadedmetadata = null;
                video.ontimeupdate = null;
                URL.revokeObjectURL(objectUrl);
            };
            const fail = (message: string) => {
                if (settled) { return; }
                settled = true;
                clearTimeout(timer);
                cleanup();
                observer.error(new Error(message));
            };
            const succeed = (metadata: VideoMetaData) => {
                if (settled) { return; }
                settled = true;
                clearTimeout(timer);
                cleanup();
                observer.next(metadata);
                observer.complete();
            };

            const timer = setTimeout(() => fail('Timed out reading video metadata'), VideoRecordingService.METADATA_TIMEOUT_MS);

            video.onerror = () => fail('Failed to load video metadata');

            video.onloadedmetadata = () => {
                // Forcing video length calculation by seeking to the end
                video.currentTime = Number.MAX_SAFE_INTEGER;
                video.ontimeupdate = () => succeed({
                    name: name,
                    deviceId: deviceId,
                    size: videoBlob.size,
                    type: videoBlob.type,
                    duration: video.duration * 1000, // milliseconds
                    width: video.videoWidth,
                    height: video.videoHeight
                });
            };

            // Teardown on unsubscribe so a still-pending read releases its resources.
            return () => {
                clearTimeout(timer);
                cleanup();
            };
        });
    }
}
